import { Controller } from "@hotwired/stimulus";

// One instance per addon card in step3/_addon.html.erb. Replaces the old
// jQuery-UI-free but fancybox-dependent "confirm before selecting yes/no"
// popups: `data-popup-enabled`/`data-confirm-popup-enabled` mean the radio
// click must be confirmed in a native modal before it actually takes
// effect, matching the original's addonYes()/addonNo() window functions
// (which just clicked the real hidden radio once confirmed).
export default class extends Controller {
  static targets = ["radioNo", "radioYes", "yesText", "popup", "confirmPopup", "personalNumber", "address"];
  static values = { popupEnabled: Boolean, confirmPopupEnabled: Boolean };

  connect() {
    this.sync();
  }

  // A radio's .checked already reflects the NEW (post-click) state by the
  // time this handler runs -- the browser's pre-click activation steps
  // update it before dispatching the click event at all, for both the
  // very first click and any repeat click on an already-checked radio
  // (confirmed empirically: .checked reads true inside a click listener
  // even on an untouched, previously-unchecked radio). A `!radio.checked`
  // guard here can therefore never be true, so the popup this method
  // exists to show never actually appeared. preventDefault() still
  // reliably reverts the checked state afterward (also confirmed
  // empirically), so showing it unconditionally when enabled is both
  // correct and sufficient.
  clickNo(event) {
    if (this.popupEnabledValue) {
      event.preventDefault();
      this.popupTarget.classList.remove("hidden");
      return;
    }
    this.sync();
  }

  clickYes(event) {
    if (this.confirmPopupEnabledValue) {
      event.preventDefault();
      this.confirmPopupTarget.classList.remove("hidden");
      return;
    }
    this.sync();
  }

  confirmYes() {
    this.radioYesTarget.checked = true;
    this.closePopups();
    this.sync();
  }

  confirmNo() {
    this.radioNoTarget.checked = true;
    this.closePopups();
    this.sync();
  }

  closePopups() {
    if (this.hasPopupTarget) this.popupTarget.classList.add("hidden");
    if (this.hasConfirmPopupTarget) this.confirmPopupTarget.classList.add("hidden");
  }

  // Wired on each popup's own backdrop -- event.currentTarget is that
  // backdrop, event.target is whatever was actually clicked, equal only
  // when the click didn't bubble up from the card content inside it.
  // Dismisses without confirming yes/no, same as not answering at all.
  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) this.closePopups();
  }

  sync() {
    const checked = this.radioYesTarget.checked;
    if (this.hasPersonalNumberTarget) this.personalNumberTarget.classList.toggle("hidden", !checked);
    if (this.hasAddressTarget) this.addressTarget.classList.toggle("hidden", !checked);
    if (this.hasYesTextTarget) {
      this.yesTextTarget.textContent = checked
        ? this.yesTextTarget.dataset.addedLabel
        : this.yesTextTarget.dataset.defaultLabel;
    }
    this.element.dispatchEvent(new CustomEvent("addon:changed", { bubbles: true }));
  }
}
